import { Dispatch, Key, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Column as RdgColumn } from "react-data-grid";

import { EditFileBlueIcon, EllipseGroupBlueIcon, TransactionsBlueIcon, TrashDark } from "../../../../../assets";
import {
  ColorConstants,
  InteractionObjectType,
  InteractionType,
  LocalStorageConstants,
  OrganisationTypeConstants,
  ProjectIssuanceConstants,
  ProjectPermissionConstants,
  UserType,
} from "../../../../../constants";
import {
  CursorChangeProps,
  DataGridColumnCurrency,
  ProjectIssuanceBNG,
  ProjectIssuanceBNGPanelData,
  ProjectIssuanceCarbon,
  ProjectIssuanceCarbonPanelData,
  ProjectIssuanceNN,
  ProjectIssuanceNNPanelData,
  ProjectIssuanceSpecificSchema,
  ResultType,
  SelectData,
} from "../../../../../models";
import {
  getProjectIssuances,
  GetProjectIssuancesResponse,
  getStandardSpecificSchemaDetails,
  searchInteractions,
} from "../../../../../service/query";
import { ResultData, Status } from "../../../../../service/Shared";
import { parseIssuanceSchema } from "../../../../../templates/IssuancePanel/utils";
import { useDeepLinkedIssuanceTransaction } from "../../../../../templates/UnitTransactionsPanel";
import { useAuth } from "../../../../../useAuth";
import {
  flattenObject,
  getHabitatFromStandardSpecificIssuanceData,
  getInventoryChartShape,
  getInventoryDefaultSort,
  getInventoryVerifiedColours,
  getInventoryVerifiedPredicate,
  getInventoryXAxisLabel,
  getNutrientChartColours,
  jsonReviver,
  recursivelyValidateAndTransformDateToString,
  useUpdateEffect,
} from "../../../../../utils";
import {
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  dataGridMapFilterCriteria,
  ProfileMenuItemType,
  StackedChartData,
  Toast,
} from "../../../../../widget";
import { ActionsIconCellFormatterData, EditColumns } from "../../../../../widget/data/DataGrid/models";
import { ProjectContext } from "../ProjectContext";

/*
  How many of the project's issuances the tag filter reads to work out which tags to offer. Matches
  getProjectIssuances' server-side MaxPageSize, so it is as much as one request can return.
*/
const TAG_SCOPE_ISSUANCES_LIMIT = 1000;

interface UseInventoryReturnData {
  isEditable: boolean;
  selectedGetProjectIssuancesResponseResults: GetProjectIssuancesResponse["results"] | undefined;
  onChange: DataGridCursorDataLoadEventHandler;
  isLoading: boolean;
  hasData: boolean;
  hasEnoughIssuancesForScrolling: boolean;
  columns: DataGridColumnDefinition[];
  unitMeasurement: string;
  stackedChartData: StackedChartData[] | undefined;
  exportFileName: string;
  xAxisLabels: (string | string[])[];
  chartKeyNutrients: string[];
  currentUserType: UserType;
  defaultEditColumns: EditColumns;
  inventoryEditColumnsLocalStorageItemName: string;
  actionsMenuOptionsList: ProfileMenuItemType[];
  exportTable: boolean | undefined;
  filterExportRows: (columns: ResultData[]) => ResultData[];
  filterExportColumns: (columns: RdgColumn<ResultData, unknown>[]) => RdgColumn<ResultData, unknown>[];

  showActionsMenu: boolean;
  setShowActionsMenu: Dispatch<SetStateAction<boolean>>;

  showEditColumnsPanel: boolean;
  setShowEditColumnsPanel: Dispatch<SetStateAction<boolean>>;

  showIssuancePanel: boolean;
  setShowIssuancePanel: (show: boolean) => void;

  hasIssuanceSchema: boolean;
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined;

  issuanceUuid: string | undefined;
  issuance: ProjectIssuanceBNGPanelData | ProjectIssuanceCarbonPanelData | ProjectIssuanceNNPanelData | undefined;
  getProjectIssuanceResult: GetProjectIssuancesResponse["results"][0] | undefined;
  handleCloseIssuancePanel: () => void;

  showUnitTransactionsPanel: boolean;
  handleCloseUnitTransactionsPanel: () => void;

  showMultiIssuanceTransactionPanel: boolean;
  setShowMultiIssuanceTransactionPanel: Dispatch<SetStateAction<boolean>>;

  deleteIssuanceRowVersion: number | undefined;
  showDeleteIssuanceModal: boolean;
  handleCloseDeleteIssuanceModal: () => void;

  handleCloseMultiIssuanceTransactionPanel: () => void;

  refresh: boolean;
  handleRefresh: () => void;
  selectedRows: ReadonlySet<Key>;
  setSelectedRows: Dispatch<SetStateAction<ReadonlySet<Key>>> | undefined;

  tagsFilterSelectData: SelectData;
  selectedTagKeys: string[];
  onTagsFilter: (keys: string[]) => Promise<void>;
  selectedStatuses: string[];
}

const NUTRIENT_UNIT = ProjectIssuanceConstants.ISSUANCE_NUTRIENT_UNIT_LABEL;

/*
  The Nutrient Neutrality grid. Deliberately carries NO vintage (NN issuances have null vintage
  dates, which is what rendered "undefined - undefined"), no habitat columns, no area or unit-length
  column, no NCA - the NN issuance schema does not declare one - and no verification status, since
  NN has no verified/pending concept.

  `quantityType` is first and is named "Nutrient" rather than "Unit type". It must stay first and
  non-summed: the totals row labels itself by writing "Total" into the first visible column that is
  not in totalColumnNames.

  LPA and LNRS are present but hidden by default - getEditColumns forces show:false for both, so
  they appear unchecked under Table options -> Edit columns.
*/
const getNutrientNeutralityColumns = (): DataGridColumnDefinition[] => [
  {
    name: "Nutrient",
    key: "quantityType",
    dataType: "string",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.eligibility,
    key: "eligibility",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.yearsEligible,
    key: "yearsEligible",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 160,
  },
  {
    name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.designatedSite,
    key: "designatedSite",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.lpa,
    key: "lpa",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.lnrs,
    key: "lnrs",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Serial Number",
    key: "serialNumber",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Issuance date",
    key: "issuanceDate",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 150,
    filterable: false,
    sortable: true,
  },
  {
    name: `Available (${NUTRIENT_UNIT})`,
    key: "unitQuantityAvailable",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 140,
  },
  {
    name: `Reserved (${NUTRIENT_UNIT})`,
    key: "unitQuantityReserved",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 140,
  },
  {
    name: `Quoted (${NUTRIENT_UNIT})`,
    key: "quotedUnits",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 140,
  },
  {
    name: `Sold (${NUTRIENT_UNIT})`,
    key: "unitQuantitySold",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 140,
  },
  {
    name: "% Sold",
    key: "percentageSold",
    formatter: "percentage",
    dataType: "number",
    filterable: false,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Price",
    key: "price",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    minWidth: 100,
    filterable: true,
    sortable: true,
  },
  {
    name: "Verifier",
    key: "verifier.displayName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Actions",
    key: "inventoryActions",
    dataType: "string",
    formatter: "actionsIcon",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 70,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Last updated",
    key: "updatedAt",
    dataType: "Date",
    formatter: "dateOnly",
    filterable: false,
    sortable: true,
    minWidth: 140,
  },
  {
    name: "Issuance ID",
    key: "issuanceUuid",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: false,
    sortable: false,
    minWidth: 320,
  },
];

/** The Biodiversity column set. Unchanged by KAN-9743 - moved verbatim out of getColumns. */
const getBiodiversityColumns = (): DataGridColumnDefinition[] => [
  {
    name: "Habitat group",
    key: "habitatType",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Habitat",
    key: "habitat",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Distinctiveness",
    key: "distinctiveness",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.lpa,
    key: "lpa",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.nca,
    key: "nca",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.lnrs,
    key: "lnrs",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Serial Number",
    key: "serialNumber",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Issuance date",
    key: "issuanceDate",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 150,
    filterable: false,
    sortable: true,
  },
  {
    name: "Available",
    key: "unitQuantityAvailable",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Reserved",
    key: "unitQuantityReserved",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Quoted",
    key: "quotedUnits",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Sold",
    key: "unitQuantitySold",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "% Sold",
    key: "percentageSold",
    formatter: "percentage",
    dataType: "number",
    filterable: false,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Price",
    key: "price",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    minWidth: 100,
    filterable: true,
    sortable: true,
  },
  {
    name: "Unit type",
    key: "quantityType",
    dataType: "string",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Verifier",
    key: "verifier.displayName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Actions",
    key: "inventoryActions",
    dataType: "string",
    formatter: "actionsIcon",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 70,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 120,
    filterable: true,
    sortable: true,
  },
  {
    name: "Unit size (Ha)",
    key: "unitHa",
    dataType: "number",
    formatter: "number",
    minWidth: 120,
    filterable: true,
    sortable: true,
  },
  {
    name: "Unit size (Km)",
    key: "unitKm",
    dataType: "number",
    formatter: "number",
    minWidth: 120,
    filterable: true,
    sortable: true,
  },
  {
    name: "Last updated",
    key: "updatedAt",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 140,
    filterable: false,
    sortable: true,
  },
  {
    name: "Issuance ID",
    key: "issuanceUuid",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 320,
    filterable: false,
    sortable: false,
  },
  {
    name: "Created or enhanced",
    key: "createdOrEnhanced",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: false,
    sortable: false,
  },
];

/**
 * The Carbon column set, which Ecosystem recovery and any unrecognised measurement also take.
 * Unchanged by KAN-9743 - moved verbatim out of getColumns.
 */
const getCarbonColumns = (): DataGridColumnDefinition[] => [
  {
    name: "Vintage",
    key: "vintage",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: false,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Serial Number",
    key: "serialNumber",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
    minWidth: 200,
  },
  {
    name: "Available",
    key: "unitQuantityAvailable",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Reserved",
    key: "unitQuantityReserved",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Quoted",
    key: "quotedUnits",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Sold",
    key: "unitQuantitySold",
    dataType: "number",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "% Sold",
    key: "percentageSold",
    formatter: "percentage",
    dataType: "number",
    filterable: false,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Unit type",
    key: "quantityType",
    dataType: "string",
    filterable: true,
    sortable: true,
    minWidth: 120,
  },
  {
    name: "Price",
    key: "price",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    minWidth: 100,
    filterable: true,
    sortable: true,
  },
  {
    name: "Verification status",
    key: "verificationStatus",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 180,
    filterable: true,
    sortable: true,
  },
  {
    name: "Verifier",
    key: "verifier.displayName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Actions",
    key: "inventoryActions",
    dataType: "string",
    formatter: "actionsIcon",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 70,
  },
  {
    name: "Issuance date",
    key: "issuanceDate",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 150,
    filterable: false,
    sortable: true,
  },
  {
    name: "Status",
    key: "status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 120,
    filterable: true,
    sortable: true,
  },
  {
    name: "Last updated",
    key: "updatedAt",
    dataType: "Date",
    formatter: "dateOnly",
    minWidth: 140,
    filterable: false,
    sortable: true,
  },
  {
    name: "Issuance ID",
    key: "issuanceUuid",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 320,
    filterable: false,
    sortable: false,
  },
];

/*
  Restoration (Restore 30x30, KAN-9934): one issuance per project with a single unit type and no
  vintage or verification status, so it is the Carbon set without Vintage, Verification status or
  Verifier, led by the unit type. The issuance date stays, and getEditColumns shows it because no
  vintage column is rendered.
*/
const RESTORATION_OMITTED_COLUMN_KEYS = ["vintage", "verificationStatus", "verifier.displayName"];

const getRestorationColumns = (): DataGridColumnDefinition[] => {
  const columns = getCarbonColumns().filter((column) => !RESTORATION_OMITTED_COLUMN_KEYS.includes(column.key));

  return [
    ...columns.filter((column) => column.key === "quantityType"),
    ...columns.filter((column) => column.key !== "quantityType"),
  ];
};

export const getColumns = (unitMeasurement: string): DataGridColumnDefinition[] => {
  switch (unitMeasurement) {
    case ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY:
      return getNutrientNeutralityColumns();
    case ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_RESTORATION:
      return getRestorationColumns();
    case ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_BIODIVERSITY:
      return getBiodiversityColumns();
    /*
      Carbon, Ecosystem recovery, and any measurement this grid does not recognise. Reachable and
      deliberate: it is also where the projectDetails `?? "Carbon"` fallback lands.
    */
    case ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_CARBON:
    case ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_ECOSYSTEM_RECOVERY:
    default:
      return getCarbonColumns();
  }
};

export const getEditColumns = (unitMeasurement: string, userType: UserType): EditColumns => {
  const columns = getColumns(unitMeasurement);

  /*
    Whether this grid actually renders a vintage column, read off the resolved column set rather
    than inferred from the measurement. An unrecognised measurement falls back to the Carbon set,
    vintage included, so deriving it here keeps that case hiding the issuance date exactly as it
    always has - and the two can never drift apart as measurements are added.
  */
  const showsVintage = columns.some((col) => col.key === "vintage");

  const editColumns = columns.reduce((acc: EditColumns, col, index) => {
    let show = true;
    if (
      col.key === "status" ||
      col.key === "unitHa" ||
      col.key === "unitKm" ||
      col.key === "createdOrEnhanced" ||
      col.key === "lpa" ||
      col.key === "nca" ||
      col.key === "lnrs" ||
      col.key === "updatedAt" ||
      col.key === "issuanceUuid" ||
      (col.key === "inventoryActions" &&
        userType !== OrganisationTypeConstants.DEVELOPER &&
        userType !== OrganisationTypeConstants.DEVELOPMENT_MANAGER) ||
      // Hide the issuance date only where the grid already shows a vintage. Nutrient Neutrality has
      // no vintage column, so it keeps its issuance date; Carbon and Ecosystem recovery do not.
      (col.key === "issuanceDate" && showsVintage)
    )
      show = false;
    acc[col.key] = { show, index };
    return acc;
  }, {});

  return editColumns;
};

const filterExportRows = (rows: ResultData[]): ResultData[] => {
  // Clone rows and Covert from date to string in dd/mm/yyyy format
  const rowsClone = recursivelyValidateAndTransformDateToString(JSON.parse(JSON.stringify(rows), jsonReviver))
    .data as ResultData[];
  rowsClone.forEach((row) => {
    // eslint-disable-next-line no-param-reassign
    if (row.inventoryActions !== undefined) delete row.inventoryActions;
  });
  return rowsClone;
};
const filterExportColumns = (rdgColumns: RdgColumn<ResultData, unknown>[]): RdgColumn<ResultData, unknown>[] => {
  return rdgColumns.filter((column) => column.key !== "inventoryActions");
};

export interface ProjectInventoryChartData {
  xAxisLabels: (string | string[])[];
  stackedChartData: StackedChartData[];
  /**
   * The nutrients drawn, in bar order, so the chart key can render one colour block per nutrient in
   * the same order and with the same palette. Empty for every measurement other than Nutrient
   * Neutrality, which is what tells the key to fall back to its verified/pending layout.
   */
  chartKeyNutrients: string[];
}

const SURFACE = "project";

export const buildProjectInventoryChartData = (
  results: GetProjectIssuancesResponse["results"],
  unitMeasurement: string
): ProjectInventoryChartData => {
  const shape = getInventoryChartShape(SURFACE, unitMeasurement);
  const isVerifiedIssuance = getInventoryVerifiedPredicate(shape.verifiedBy);

  const keys: (string | string[])[] = [];
  const verifiedSold: number[] = [];
  const verifiedReserved: number[] = [];
  const verifiedAvailable: number[] = [];
  const pendingSold: number[] = [];
  const pendingReserved: number[] = [];
  const pendingAvailable: number[] = [];

  /*
    Nutrient Neutrality: one bar per nutrient, aggregated - two issuances of the same nutrient fold
    into one bar. Restoration takes this branch too, drawing one bar per unit type - in practice the
    single "30x30 Unit" - with the same verified-only fill. Groups on the quantityType node field rather than parsing the blob, and pushes the
    nutrient as a plain string rather than a string[], so the label stays on one line.

    NN has no verified/pending split, so only the verified* arrays are populated and the chart emits
    three series. This branch deliberately does not read the quantityType verified test the Carbon
    branch uses.
  */
  if (shape.groupBy === "quantityType") {
    const groupedResults: {
      [nutrient: string]: {
        unitQuantitySold: number;
        unitQuantityReserved: number;
        unitQuantityAvailable: number;
      };
    } = {};

    results.forEach((value) => {
      const nutrient = value.quantityType;

      if (!groupedResults[nutrient]) {
        groupedResults[nutrient] = { unitQuantitySold: 0, unitQuantityReserved: 0, unitQuantityAvailable: 0 };
      }

      groupedResults[nutrient].unitQuantitySold += value.unitQuantitySold;
      groupedResults[nutrient].unitQuantityReserved += value.unitQuantityReserved;
      groupedResults[nutrient].unitQuantityAvailable += value.unitQuantityAvailable;
    });

    Object.keys(groupedResults).forEach((nutrient) => {
      keys.push(nutrient);
      verifiedSold.push(groupedResults[nutrient].unitQuantitySold);
      verifiedReserved.push(groupedResults[nutrient].unitQuantityReserved);
      verifiedAvailable.push(groupedResults[nutrient].unitQuantityAvailable);
    });
  } else if (shape.groupBy === "habitat") {
    const groupedResults: {
      [key: string]: {
        unitQuantitySold: number;
        unitQuantityReserved: number;
        unitQuantityAvailable: number;
      };
    } = {};

    results.forEach((value) => {
      const habitatType = getHabitatFromStandardSpecificIssuanceData(value.standardSpecificIssuanceData).join("-");

      if (!groupedResults[habitatType]) {
        groupedResults[habitatType] = {
          unitQuantitySold: 0,
          unitQuantityReserved: 0,
          unitQuantityAvailable: 0,
        };
      }

      groupedResults[habitatType].unitQuantitySold += value.unitQuantitySold;
      groupedResults[habitatType].unitQuantityReserved += value.unitQuantityReserved;
      groupedResults[habitatType].unitQuantityAvailable += value.unitQuantityAvailable;
    });

    Object.keys(groupedResults).forEach((key: string) => {
      keys.push(key.split("-"));
      verifiedSold.push(groupedResults[key].unitQuantitySold);
      verifiedReserved.push(groupedResults[key].unitQuantityReserved);
      verifiedAvailable.push(groupedResults[key].unitQuantityAvailable);
    });
  } else {
    results.forEach((value) => {
      keys.push(getInventoryXAxisLabel(SURFACE, value));
      const isVerified = isVerifiedIssuance(value);
      verifiedSold.push(isVerified ? value.unitQuantitySold : 0);
      verifiedReserved.push(isVerified ? value.unitQuantityReserved : 0);
      verifiedAvailable.push(isVerified ? value.unitQuantityAvailable : 0);
      pendingSold.push(isVerified ? 0 : value.unitQuantitySold);
      pendingReserved.push(isVerified ? 0 : value.unitQuantityReserved);
      pendingAvailable.push(isVerified ? 0 : value.unitQuantityAvailable);
    });
  }

  /*
    Nutrient Neutrality emits three series, not six - the pending arrays are empty, and three empty
    datasets would otherwise be drawn. The colours are the verified triple below, which is what the
    chart key renders for this measurement.
  */
  if (
    shape.groupBy === "quantityType" &&
    unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY
  ) {
    /*
      Gated on the measurement as well as the shape: Restoration shares the shape but has no
      nutrients, so it falls through to the six-series return in its own colour family, with the
      pending three empty.

      Each nutrient carries its own colour family rather than the shared GREEN triple, so a series'
      colour varies per bar: Chart.js takes an array of colours indexed by category for exactly this.
      The palettes come from getNutrientChartColours so the key and the bars cannot drift apart.
    */
    const nutrients = keys as string[];
    const palettes = nutrients.map((nutrient, index) => getNutrientChartColours(nutrient, index));

    return {
      xAxisLabels: keys,
      chartKeyNutrients: nutrients,
      stackedChartData: [
        {
          label: "Sold",
          data: verifiedSold,
          backgroundColor: palettes.map((palette) => palette.dark),
          hoverBackgroundColor: palettes.map((palette) => palette.darkHover),
        },
        {
          label: "Reserved",
          data: verifiedReserved,
          backgroundColor: palettes.map((palette) => palette.mid),
          hoverBackgroundColor: palettes.map((palette) => palette.midHover),
        },
        {
          label: "Available",
          data: verifiedAvailable,
          backgroundColor: palettes.map((palette) => palette.light),
          hoverBackgroundColor: palettes.map((palette) => palette.lightHover),
        },
      ],
    };
  }

  const verifiedColours = getInventoryVerifiedColours(unitMeasurement);

  return {
    xAxisLabels: keys,
    chartKeyNutrients: [],
    stackedChartData: [
      {
        label: "Sold",
        data: pendingSold,
        backgroundColor: ColorConstants.INDIGO_500,
        hoverBackgroundColor: ColorConstants.INDIGO_400,
      },
      {
        label: "Reserved",
        data: pendingReserved,
        backgroundColor: ColorConstants.INDIGO_300,
        hoverBackgroundColor: ColorConstants.INDIGO_200,
      },
      {
        label: "Available",
        data: pendingAvailable,
        backgroundColor: ColorConstants.INDIGO_100,
        hoverBackgroundColor: ColorConstants.BLUE_100,
      },
      {
        label: "Sold",
        data: verifiedSold,
        backgroundColor: verifiedColours.dark,
        hoverBackgroundColor: verifiedColours.darkHover,
      },
      {
        label: "Reserved",
        data: verifiedReserved,
        backgroundColor: verifiedColours.mid,
        hoverBackgroundColor: verifiedColours.midHover,
      },
      {
        label: "Available",
        data: verifiedAvailable,
        backgroundColor: verifiedColours.light,
        hoverBackgroundColor: verifiedColours.lightHover,
      },
    ],
  };
};

export const useInventory = (): UseInventoryReturnData => {
  const { currentUserType, user, currentOrganisationUuid } = useAuth();
  const { projectDetails, shouldRefreshProjectDetails, hasProjectPermission, selectedStatuses } =
    useContext(ProjectContext);
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [hasEnoughIssuancesForScrolling, setHasEnoughIssuancesForScrolling] = useState(false);

  const [stackedChartData, setStackedChartData] = useState<StackedChartData[]>();
  const [xAxisLabels, setXAxisLabels] = useState<(string | string[])[]>([]);
  const [chartKeyNutrients, setChartKeyNutrients] = useState<string[]>([]);

  const [showIssuancePanel, setShowIssuancePanel] = useState(false);
  const [showDeleteIssuanceModal, setShowDeleteIssuanceModal] = useState(false);
  const [showEditColumnsPanel, setShowEditColumnsPanel] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showUnitTransactionsPanel, setShowUnitTransactionsPanel] = useState(false);
  const [showMultiIssuanceTransactionPanel, setShowMultiIssuanceTransactionPanel] = useState(false);

  const [getProjectIssuanceResult, setGetProjectIssuanceResult] = useState<GetProjectIssuancesResponse["results"][0]>();
  const [issuance, setIssuance] = useState<
    ProjectIssuanceBNGPanelData | ProjectIssuanceCarbonPanelData | ProjectIssuanceNNPanelData
  >();
  const [issuanceUuid, setIssuanceUuid] = useState<string>();
  const [deleteIssuanceRowVersion, setDeleteIssuanceRowVersion] = useState<number | undefined>(undefined);

  const [getProjectIssuancesResponseResults, setGetProjectIssuancesResponseResults] =
    useState<GetProjectIssuancesResponse["results"]>();

  /*
    The transactions panel is opened with the row that was clicked, but adding, editing or progressing a
    transaction changes the issuance's own figures (available, reserved, sold, quoted) and can change its
    status. Those edits refresh the grid, so read the summary from the latest fetched row and fall back to
    the snapshot only until that fetch lands.
  */
  const unitTransactionsIssuance = useMemo(
    () =>
      getProjectIssuancesResponseResults?.find((el) => el.uuid === getProjectIssuanceResult?.uuid) ??
      getProjectIssuanceResult,
    [getProjectIssuancesResponseResults, getProjectIssuanceResult]
  );
  const [selectedGetProjectIssuancesResponseResults, setSelectedGetProjectIssuancesResponseResults] =
    useState<GetProjectIssuancesResponse["results"]>();

  const [refresh, setRefresh] = useState(false);
  const [exportTable, setExportTable] = useState<boolean>();

  // Reload the grid and chart when the project changes in place (the tab can stay mounted across a :projectUuid change).
  useUpdateEffect(() => setRefresh((prev) => !prev), [projectDetails?.uuid]);
  /*
    The selection now lives on ProjectContext, so it can change from outside this hook. An effect
    rather than a refresh inside the setter, so an external change refetches too.
  */
  useUpdateEffect(() => setRefresh((prev) => !prev), [selectedStatuses]);

  const [issuanceSchema, setIssuanceSchema] = useState<ProjectIssuanceSpecificSchema>();
  const hasIssuanceSchema = issuanceSchema !== undefined;

  const [selectedRows, setSelectedRows] = useState<ReadonlySet<Key>>(new Set());
  const [tagsFilter, setTagsFilter] = useState<string[]>();
  const [tagsFilterSelectData, setTagsFilterSelectData] = useState<SelectData>([]);
  const [selectedTagKeys, setSelectedTagKeys] = useState<string[]>([]);

  /*
    Read by the tag-options effect to prune a selection the options no longer contain. Held in a ref so
    the effect does not re-read the options every time the selection changes.
  */
  const selectedTagKeysRef = useRef(selectedTagKeys);
  useEffect(() => {
    selectedTagKeysRef.current = selectedTagKeys;
  }, [selectedTagKeys]);

  const isEditable = hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES);

  /*
    A quote-expiry reminder links to this tab naming its quote, so the recipient lands on that quote's
    transactions rather than having to find the vintage row and open the panel by hand.
  */
  const deepLinkedIssuance = useDeepLinkedIssuanceTransaction(projectDetails?.uuid);

  /*
    Keyed on the resolved issuance alone: the link has already been taken out of the URL by the time it
    arrives, so closing the panel cannot reopen it and neither can a grid refresh.
  */
  useEffect(() => {
    if (!deepLinkedIssuance) return;

    setGetProjectIssuanceResult(deepLinkedIssuance);
    setShowUnitTransactionsPanel(true);
  }, [deepLinkedIssuance]);

  const unitMeasurement = useMemo(
    () => projectDetails?.unitMeasurements?.[0] ?? ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_CARBON,
    [projectDetails?.unitMeasurements]
  );

  const actionsMenuOptionsList: ProfileMenuItemType[] = [
    {
      id: 1,
      value: "Edit columns",
      action: () => setShowEditColumnsPanel(true),
    },
    {
      id: 2,
      value: "Export table",
      action: () => setExportTable(!exportTable),
    },
  ];

  const handleCloseIssuancePanel = (): void => {
    setShowIssuancePanel(false);
    setIssuance(undefined);
  };

  const handleCloseDeleteIssuanceModal = (): void => {
    setIssuanceUuid(undefined);
    setShowDeleteIssuanceModal(false);
  };

  const handleCloseUnitTransactionsPanel = (): void => {
    setIssuanceUuid(undefined);
    setShowUnitTransactionsPanel(false);
  };

  const handleCloseMultiIssuanceTransactionPanel = (): void => {
    setSelectedRows(new Set());
    setShowMultiIssuanceTransactionPanel(false);
  };

  const handleRefresh = (): void => {
    setRefresh((prev) => !prev);
    shouldRefreshProjectDetails();
  };

  const formatData = useCallback(
    (responseData: GetProjectIssuancesResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.issuanceUuid = el.uuid;

        const standardSpecificIssuanceData = JSON.parse(
          el.standardSpecificIssuanceData,
          jsonReviver
          // Every measurement's blob, so the edit path pre-populates whichever fields it carries.
        ) as ProjectIssuanceCarbon & ProjectIssuanceBNG & ProjectIssuanceNN;

        if (isEditable) {
          result.inventoryActions = {
            icon: <EllipseGroupBlueIcon />,
            actions: [
              {
                id: 1,
                value: "Edit",
                icon: <EditFileBlueIcon width={20} height={20} />,
                action: () => {
                  setShowIssuancePanel(true);
                  setIssuance({
                    uuid: el.uuid,
                    price: el.price,
                    serialNumber: el.serialNumber,
                    quantityType: el.quantityType,
                    soldQuantity: el.unitQuantitySold,
                    reservedQuantity: el.unitQuantityReserved,
                    availableQuantity: el.unitQuantityAvailable,
                    quotedQuantity: el.quotedUnits,
                    status: el.status,
                    issuanceDate: el.issuanceDate,
                    standardSpecificIssuanceData,
                    rowVersion: el.rowVersion,
                    verifierUuid: el.verifier == null ? null : el.verifier.uuid,
                  });
                  setDeleteIssuanceRowVersion(el.rowVersion);
                },
              },
              {
                id: 2,
                value: "Delete",
                icon: <TrashDark width={20} height={20} className="InventoryDeleteIcon" />,
                action: () => {
                  setShowDeleteIssuanceModal(true);
                  setIssuanceUuid(el.uuid);
                  setDeleteIssuanceRowVersion(el.rowVersion);
                },
              },
              {
                id: 3,
                value: "Transactions",
                icon: <TransactionsBlueIcon />,
                action: () => {
                  setShowUnitTransactionsPanel(true);
                  setGetProjectIssuanceResult(el);
                },
              },
            ],
          } as ActionsIconCellFormatterData;
        }

        switch (unitMeasurement) {
          case ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_CARBON:
          case ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_ECOSYSTEM_RECOVERY:
            result.vintage = `${el.vintageStartDate?.getFullYear()} - ${el.vintageEndDate?.getFullYear()}`;
            break;
          default:
            break;
        }
        return result;
      }) || [],
    []
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    setIsLoading(true);
    const filterCriteria = dataGridMapFilterCriteria(filtering);
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const sortKeyMap: Record<string, string> = {
      vintage: "vintageStartDate",
      issuanceUuid: "uuid",
    };

    if (tagsFilter && tagsFilter.length > 0) {
      filterCriteria.uuid = {
        operator: "in",
        value: tagsFilter,
      };
    }

    if (selectedStatuses.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (filterCriteria as any).status = {
        operator: "in",
        value: selectedStatuses,
      };
    }

    if (projectDetails)
      await getProjectIssuances({
        paging: {
          beforeCursor: paging.beforeCursor || null,
          afterCursor: paging.afterCursor || null,
          limit: paging.pageSize,
        },
        /* eslint-disable @typescript-eslint/no-explicit-any */
        sort:
          sorting.length > 0
            ? sorting.map((sort: { key: any; direction: any }) => ({
                key: sortKeyMap[sort.key] || sort.key,
                direction: sort.direction,
              }))
            : [
                {
                  key: getInventoryDefaultSort(SURFACE, unitMeasurement).serverSortKey,
                  direction: "ASC",
                },
              ],
        /* eslint-enable */
        filter: { results: filterCriteria },
        request: { projectUuid: projectDetails.uuid, groupUuid: null },
      })
        .then((response) => {
          data = {
            resultData: formatData(response.data),
            paging: {
              startCursor: response.data?.paging?.startCursor || "",
              endCursor: response.data?.paging?.endCursor || "",
              pageSize: paging.pageSize,
              totalCount: response.data?.paging?.total || 0,
              hasNextPage: response.data?.paging?.hasNextPage || false,
              hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
            },
          };

          const results = response.data?.results ?? [];
          const chart = buildProjectInventoryChartData(results, unitMeasurement);

          setHasData(results.length > 0);
          setHasEnoughIssuancesForScrolling(results.length > 5);
          setXAxisLabels(chart.xAxisLabels);
          setChartKeyNutrients(chart.chartKeyNutrients);
          setStackedChartData(chart.stackedChartData);
          setGetProjectIssuancesResponseResults(response.data?.results);
        })
        .finally(() => {
          setIsLoading(false);
        });

    return data;
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (projectDetails?.standardSpecificSchemaUuid) {
        await getStandardSpecificSchemaDetails({
          standardSpecificSchemaUuid: projectDetails.standardSpecificSchemaUuid,
        }).then((response) => {
          if (response.status === Status.Success && response.data) {
            setIssuanceSchema(parseIssuanceSchema(response.data.issuanceSchema));
          }
        });
      }
    };

    fetchData();
  }, [projectDetails?.standardSpecificSchemaUuid]);

  useEffect(() => {
    setSelectedGetProjectIssuancesResponseResults(
      getProjectIssuancesResponseResults?.filter((i) => selectedRows.has(i.uuid))
    );
  }, [getProjectIssuancesResponseResults, selectedRows]);

  // Resolves the selected tags into the issuance uuids the grid filters on.
  const resolveTagsFilter = useCallback(async (keys: string[]): Promise<void> => {
    if (keys.length === 0) {
      setTagsFilter(undefined);
      setRefresh((prev) => !prev);
      return;
    }

    const searchInteractionsFilterCriteria = dataGridMapFilterCriteria([]);

    searchInteractionsFilterCriteria.uuid = {
      operator: "in",
      value: keys,
    };

    await searchInteractions({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      filter: { results: searchInteractionsFilterCriteria },
    }).then((response) => {
      if (response.data?.results != null && response.data.results.length > 0) {
        setTagsFilter(
          response.data.results
            .filter((x) => x.objectInteractions.length > 0)
            .map((x) => x.objectInteractions.map((y) => y.objectUuid))
            .flat()
        );
      } else {
        setTagsFilter(undefined);
      }
      setRefresh((prev) => !prev);
    });
  }, []);

  const onTagsFilter = async (keys: string[]): Promise<void> => {
    setSelectedTagKeys(keys);
    await resolveTagsFilter(keys);
  };

  /*
    The tags the filter offers, which are the tags carried by this project's issuances. Scoping needs
    those issuances' uuids first: a tag's links can only be matched by a specific objectUuid, so there is
    no single filter for "tags on any issuance of this project". The page's own issuance state holds only
    the grid's current page, so it cannot stand in for this.
  */
  const getScopedTags = useCallback(async (organisationUuid: string, projectUuid: string): Promise<SelectData> => {
    const issuancesResponse = await getProjectIssuances({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: TAG_SCOPE_ISSUANCES_LIMIT,
      },
      request: { projectUuid, groupUuid: null },
    });

    if (issuancesResponse.status === Status.Error) {
      throw new Error("Unable to read the project's issuances");
    }

    const issuanceUuids = issuancesResponse.data?.results.map((i) => i.uuid) ?? [];

    // A project with no issuances has no tags to filter by, so there is nothing to ask for.
    if (issuanceUuids.length === 0) return [];

    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.type = {
      operator: "eq",
      value: InteractionType.TAG,
    };
    filterCriteria.objectType = {
      operator: "eq",
      value: InteractionObjectType.ORGANISATION,
    };
    filterCriteria.objectUuid = {
      operator: "eq",
      value: organisationUuid,
    };
    filterCriteria.objectInteractions = {
      some: {
        objectType: {
          operator: "eq",
          value: InteractionObjectType.ISSUANCE,
        },
        objectUuid: {
          operator: "in",
          value: issuanceUuids,
        },
        /*
            Removing a tag is a soft delete that stamps end_date on the link rather than deleting it, so
            without this a tag that has been removed from every issuance stays in the list for good.
          */
        endDate: {
          operator: "eq",
          value: null,
        },
      },
    };

    const response = await searchInteractions({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      filter: { results: filterCriteria },
    });

    if (response.status === Status.Error) {
      throw new Error("Unable to read the organisation's tags");
    }

    return (response.data?.results ?? []).map((x) => ({
      key: x.uuid,
      value: JSON.parse(x.data, jsonReviver).name,
    })) as SelectData;
  }, []);

  useEffect(() => {
    if (!currentOrganisationUuid || !projectDetails?.uuid) return undefined;

    let cancelled = false;

    getScopedTags(currentOrganisationUuid, projectDetails.uuid)
      .then((tags) => {
        if (cancelled) return;

        // Set unconditionally - an empty response has to clear the list, not leave the previous one up.
        setTagsFilterSelectData(tags);

        /*
          A tag that has just lost its last live link is gone from the options but may still be
          selected, which would otherwise keep narrowing the grid by a tag the user can no longer see.
        */
        const availableKeys = new Set(tags.map((tag) => tag.key));
        const stillSelected = selectedTagKeysRef.current.filter((key) => availableKeys.has(key));

        if (stillSelected.length !== selectedTagKeysRef.current.length) {
          setSelectedTagKeys(stillSelected);
          resolveTagsFilter(stillSelected).catch(() => {
            Toast.error({ message: "Unable to update the tag filter" });
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        Toast.error({ message: "Unable to load the tag filter" });
      });

    return () => {
      cancelled = true;
    };
  }, [currentOrganisationUuid, projectDetails?.uuid, refresh, getScopedTags, resolveTagsFilter]);

  return {
    selectedGetProjectIssuancesResponseResults,
    onChange,
    isLoading,
    hasData,
    hasEnoughIssuancesForScrolling,
    columns: getColumns(unitMeasurement),
    unitMeasurement,
    xAxisLabels,
    chartKeyNutrients,
    exportFileName: `${projectDetails?.displayName ? `${projectDetails.displayName}_` : ""}Inventory_`.replaceAll(
      " ",
      "_"
    ),
    stackedChartData,
    currentUserType,
    showIssuancePanel,
    actionsMenuOptionsList,
    exportTable,
    setShowIssuancePanel,
    showActionsMenu,
    setShowActionsMenu,
    showEditColumnsPanel,
    setShowEditColumnsPanel,
    getProjectIssuanceResult: unitTransactionsIssuance,
    issuance,
    handleCloseIssuancePanel,
    showUnitTransactionsPanel,
    handleCloseUnitTransactionsPanel,
    issuanceUuid,
    deleteIssuanceRowVersion,
    showDeleteIssuanceModal,
    handleCloseDeleteIssuanceModal,
    handleCloseMultiIssuanceTransactionPanel,
    refresh,
    handleRefresh,
    defaultEditColumns: getEditColumns(unitMeasurement, currentUserType),
    inventoryEditColumnsLocalStorageItemName: `${user?.userUuid ? `${user?.userUuid}_` : ""}${projectDetails?.unitMeasurements ? `${projectDetails?.unitMeasurements}_` : ""}${LocalStorageConstants.PROJECT_INVENTORY_PREFERENCES}`,
    filterExportColumns,
    filterExportRows,
    hasIssuanceSchema,
    issuanceSchema,
    isEditable,
    selectedRows,
    setSelectedRows: isEditable ? setSelectedRows : undefined,
    showMultiIssuanceTransactionPanel,
    setShowMultiIssuanceTransactionPanel,
    tagsFilterSelectData,
    selectedTagKeys,
    onTagsFilter,
    selectedStatuses,
  };
};
